import { Component, ElementRef, EventEmitter, Inject, Input, NgZone, OnChanges, OnInit, Output, PLATFORM_ID, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ThousandsSeparatorPipe } from '../../pipes/thousands-separator.pipe';
import { CurrenciesContainer } from 'projects/common/model/landingModel';
import { LandingModelService } from 'projects/common/services/landingModel.service';
import { isPlatformBrowser } from '@angular/common';

export interface SliderRange {
    name?: string;
    description?: string;
    icon_name?: string;
    min: number;
    max: number;
    minTime?: string;
    maxTime?: string;
    minFormatted?: string;
    maxFormatted?: string;
    step?: number;
}

export enum SliderType {
    RANGE = <any> 'RANGE',
    MIN = <any> 'MIN',
    MAX = <any> 'MAX'
}

@Component({
    selector: 'slider-range',
    template: `
      <ng-container *ngIf="sliderRange[currentIndex]">

        <span *ngIf="sliderRange[currentIndex]?.description" class="slider-range-driver eva-3-driver -secondary -eva-3-mr-md">
          <i *ngIf="sliderRange[currentIndex]?.icon_name" class="eva-3-icon-{{sliderRange[currentIndex].icon_name}} eva-icon"></i>
          <span class="driver-text"> {{sliderRange[currentIndex].description}}</span>
        </span>
        <span class="eva-3-body-2">{{sliderRange[currentIndex].name}}</span>

        <div class="eva-3-range-slider" [style]="evaSliderRangeHandlersPosition" [ngClass]="{'-eva-3-mt-lg': false}">
            <div class="slider-bar" #sliderElement>
                <div class="slider-range"></div>

                <div class="slider-handler -left" *ngIf="!isTypeMax" data-sfa-id="slider-handler"
                    (mousedown)="slideStart('min')" (touchstart)="slideStart('min')">
                </div>

                <div class="slider-handler -right" *ngIf="!isTypeMin" data-sfa-id="slider-handler"
                    (mousedown)="slideStart('max')" (touchstart)="slideStart('max')">
                </div>
            </div>
        </div>

        <div class="range-values" *ngIf="!value_type && selectedValues && selectedValues[currentIndex]">
            <em class="range-value">{{currencyMask}} {{selectedValues[currentIndex].min | thousandsSeparator: thousandsSymbol}}</em>
            <em class="range-value">{{currencyMask}} {{selectedValues[currentIndex].max | thousandsSeparator: thousandsSymbol}}</em>
        </div>

        <div class="range-values" *ngIf="(value_type === 'FARE' || value_type === 'DAYS') && selectedValues && selectedValues[currentIndex]">
            <em class="range-value">{{'filters.range.min' | translate}} {{currencyMask}} {{selectedValues[currentIndex].min | thousandsSeparator: thousandsSymbol}}</em>
            <em class="range-value">{{'filters.range.max' | translate}} {{currencyMask}} {{selectedValues[currentIndex].max | thousandsSeparator: thousandsSymbol}}</em>
        </div>

        <div class="range-values" *ngIf="value_type === 'TIME' && selectedValues && selectedValues[currentIndex]">
            <em class="range-value">{{ minutesToTime(selectedValues[currentIndex].min) }} {{'filters.range.hours' | translate}}</em>
            <em class="range-value">{{ minutesToTime(selectedValues[currentIndex].max) }} {{'filters.range.hours' | translate}}</em>
        </div>
      </ng-container>
    `,
    styleUrls: ['./slider-range.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class SliderRangeComponent implements OnInit, OnChanges {
    @Input() sliderRange: {[key: string]: SliderRange};
    @Input() preselectedRange: {[key: string]: SliderRange};
    @Input() currency: any;
    @Input() sliderType: string;
    @Input() value_type: 'FARE' | 'TIME' | 'DAYS';

    @Output() rangeAppliedEvent: EventEmitter<{[key: string]: SliderRange}> = new EventEmitter();

    @ViewChild('sliderElement') _sliderElement: ElementRef;

    selectedValues: {[key: string]: SliderRange};
    selectedValuesPercentage: SliderRange;
    evaSliderRangeHandlersPosition: SafeStyle;
    currencies: CurrenciesContainer;
    isTypeMax: boolean;
    isTypeMin: boolean;
    keys: string[];
    currentIndex: string;
    currencyMask: string;
    thousandsSymbol: string;

    private _coordinatesRange: SliderRange;
    private _currentLimitType: string;

    constructor(private _zone: NgZone,
        private domSanitizer: DomSanitizer,
        private translateService: TranslateService,
        private thousandsSeparatorPipe: ThousandsSeparatorPipe,
        private landingModelService: LandingModelService,
        @Inject(PLATFORM_ID) private platformId: string) { }

    ngOnInit() {
        if (!isPlatformBrowser(this.platformId)) return;
        
        this.currencies = this.landingModelService.getLandingModel().currencies;
        this.keys = Object.keys(this.sliderRange);

        if (this.value_type === 'FARE' || !this.value_type) {
            const currency = this.sliderRange[this.currency?.code] ? this.currency : this.currencies.secondaryCurrency;
            this.currentIndex = currency.code;
            this.currencyMask = currency.symbol;
            this.thousandsSymbol = currency.thousandsSymbol;
        } else {
          this.currentIndex = this.keys[0]; // si no es tipo price no tenemos multicurrency
          this.currencyMask = '';
        }

        if (this.value_type === 'TIME'){
          if (!this.sliderRange[this.currentIndex].min && ! this.sliderRange[this.currentIndex].max) {
            this.sliderRange[this.currentIndex].min = this.timeToMinutes(this.sliderRange[this.currentIndex].minTime || '00:00');
            this.sliderRange[this.currentIndex].max = this.timeToMinutes(this.sliderRange[this.currentIndex].maxTime || '23:59');
          }
        }

        if (!this.sliderType) {
            this.sliderType = SliderType[SliderType.RANGE];
        }

        this.setValues();
        this.setValuesPercentage();

        this.isTypeMax = this.sliderType === 'MAX';
        this.isTypeMin = this.sliderType === 'MIN';

        this.getEvaSliderRangeHandlersPosition();
    }

    ngOnChanges(changes: SimpleChanges) {
      this.keys = changes.sliderRange?.currentValue ? Object.keys(changes.sliderRange?.currentValue) : this.keys;

      if (this.value_type === 'FARE' && changes.currency && !changes.currency.firstChange && changes.currency.currentValue !== changes.currency.previousValue) {
          this.currentIndex = changes.currency.currentValue.code;
          this.currencyMask = changes.currency.currentValue.symbol;
      }
      if (changes.preselectedRange && !changes.preselectedRange.firstChange && changes.preselectedRange.currentValue !== changes.preselectedRange.previousValue) {
          this.setValues();
          this.setValuesPercentage();
          this.updateSliderRangeHandlers('min');
          this.updateSliderRangeHandlers('max');
      }
    }

    setValuesPercentage(): void {
      if (this.preselectedRange && this.preselectedRange[this.currentIndex]) {

        this.selectedValuesPercentage = {
            min: this.getPercentage(this.sliderRange[this.currentIndex], this.preselectedRange[this.currentIndex].min),
            max: this.getPercentage(this.sliderRange[this.currentIndex], this.preselectedRange[this.currentIndex].max)
        };

      } else {
          this.selectedValuesPercentage = {
              min: 0,
              max: 100
          }
      }
    }

    setValues(): void {
      this.selectedValues = {};
      const preselectionOrRange = this.preselectedRange && this.preselectedRange[this.currentIndex] ? this.preselectedRange : this.sliderRange;

      this.keys.forEach((key: string) => {
          this.selectedValues[key] = {
              min: preselectionOrRange[key].min,
              max: preselectionOrRange[key].max
          }
      });
    }

    slideStart(limitType: string): void {
        this._currentLimitType = limitType;
        if (!this._coordinatesRange) {
            let clientRect: ClientRect = this._sliderElement.nativeElement.getBoundingClientRect();

            this._coordinatesRange = {
              min: clientRect.left,
              max: clientRect.left + clientRect.width,
            };
        }

        // deny dragging and selecting
        document.ondragstart = function(): boolean {
            return false;
        };
        document.body.onselectstart = function(): boolean {
            return false;
        };

        document.onmousemove = this.dragProcess.bind(this);
        document.onmouseup = this.slideStop.bind(this);

        document.ontouchmove = this.dragProcess.bind(this);
        document.ontouchend = this.slideStop.bind(this);
    }

    dragProcess(event: any): void {
        let xValue: number = event.clientX;
        if (!xValue && event.touches && event.touches.length === 1) {
            xValue = event.touches[0].clientX;
        }

        if (xValue) {
            if (xValue < this._coordinatesRange.min) {
                xValue = this._coordinatesRange.min;
            }

            if (this._coordinatesRange.max < xValue) {
                xValue = this._coordinatesRange.max;
            }

            this._zone.run(() => {
                let percentage: number = this.getPercentage(this._coordinatesRange, xValue);
                let newValue: {[key: string]: number} = {};

                this.keys.forEach((key: string) => {
                    newValue[key] = percentage / 100 * (this.sliderRange[key].max - this.sliderRange[key].min) + this.sliderRange[key].min;
                });

                if (this._currentLimitType === 'min') {
                    if (this.selectedValues[this.currentIndex].max < newValue[this.currentIndex]) {
                        this.keys.forEach(key => newValue[key] = this.selectedValues[key].max);
                    } else if (this.sliderRange[this.currentIndex].min !== newValue[this.currentIndex]) {
                        this.keys.forEach(key => newValue[key] -= ((newValue[key] - this.sliderRange[key].min) % (this.sliderRange[key].step ?? Infinity)));
                    }
                }

                if (this._currentLimitType === 'max') {
                    if (newValue[this.currentIndex] < this.selectedValues[this.currentIndex].min) {
                        this.keys.forEach(key => newValue[key] = this.selectedValues[key].min);
                    } else if (newValue[this.currentIndex] !== this.sliderRange[this.currentIndex].max) {
                        this.keys.forEach(key => newValue[key] -= ((newValue[key] - this.sliderRange[key].min) % (this.sliderRange[key].step ?? Infinity)))
                    }
                }

                this.keys.forEach((key: string) => this.selectedValues[key][this._currentLimitType] = newValue[key]);
                this.updateSliderRangeHandlers(this._currentLimitType);
            });
        }
    }

    slideStop(): void {
        document.onmousemove = null;
        document.onmouseup = null;

        document.ontouchmove = null;
        document.ontouchend = null;

        if (this.value_type === 'TIME'){
          this.selectedValues[this.currentIndex].minTime = this.minutesToTime(this.selectedValues[this.currentIndex].min);
          this.selectedValues[this.currentIndex].minFormatted = this.selectedValues[this.currentIndex].minTime;
          this.selectedValues[this.currentIndex].maxTime = this.minutesToTime(this.selectedValues[this.currentIndex].max);
          this.selectedValues[this.currentIndex].maxFormatted = this.selectedValues[this.currentIndex].maxTime;
        } else {
          this.selectedValues[this.currentIndex].minFormatted = this.currencyMask + this.thousandsSeparatorPipe.transform(this.selectedValues[this.currentIndex].min, this.thousandsSymbol);
          this.selectedValues[this.currentIndex].maxFormatted = this.currencyMask + this.thousandsSeparatorPipe.transform(this.selectedValues[this.currentIndex].max, this.thousandsSymbol);
        }

        this.rangeAppliedEvent.emit(this.selectedValues);
    }

    getPercentage(sliderRange: SliderRange, value: number): number {
        return (value - sliderRange.min) * 100 / (sliderRange.max - sliderRange.min);
    }

    updateSliderRangeHandlers(type: string = ''): void {
        if (type === '' || type === 'min') {
            this.setSelectedValuesPercentageMin();
        }
        if (type === '' || type === 'max') {
            this.setSelectedValuesPercentageMax();
        }
        this.getEvaSliderRangeHandlersPosition();
    }

    setSelectedValuesPercentageMin(): void {
        if (this.selectedValuesPercentage) {
            this.selectedValuesPercentage.min = this.getPercentage(this.sliderRange[this.currentIndex], this.selectedValues[this.currentIndex].min);
        }
    }

    setSelectedValuesPercentageMax(): void {
        if (this.selectedValuesPercentage) {
            this.selectedValuesPercentage.max = this.getPercentage(this.sliderRange[this.currentIndex], this.selectedValues[this.currentIndex].max);
        }
    }

    getEvaSliderRangeHandlersPosition(): void {
        if (this.selectedValuesPercentage) {
            this.evaSliderRangeHandlersPosition = this.domSanitizer.bypassSecurityTrustStyle('--eva-slider-range-left-handler-position:' + this.selectedValuesPercentage.min + '%;--eva-slider-range-right-handler-position:' + this.selectedValuesPercentage.max + '%;');
        }
    }

    timeToMinutes(time: string): number {
      const [hours, minutes] = time.split(':').map(Number);
      return hours * 60 + minutes;
    }

    minutesToTime(totalMinutes: number): string {
      const hours = Math.floor(totalMinutes / 60);
      const minutes = Math.floor(totalMinutes % 60);
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }
}
